/**
 * Physical feedback on a pad hit, on both kinds of phone.
 *
 * Android and desktop Chrome have the Vibration API. iOS never has and, going by Apple's
 * position on it, never will — `navigator.vibrate` is simply absent in Safari, so a soundboard
 * installed on an iPhone had no feedback at all.
 *
 * There is exactly one way to make an iPhone tap from a web page today. iOS 17.4 shipped
 * `<input type="checkbox" switch>`, and Safari plays a light haptic tick whenever that control
 * toggles. Toggling a hidden one produces the tick without showing a switch to anybody.
 *
 * Being honest about what this is: it leans on a side effect of an accessibility control, not
 * a documented API. It is one fixed intensity, so velocity cannot modulate it and a multi-part
 * pattern collapses to a single tap. A future Safari could remove it without warning. It is
 * used because the alternative is nothing, and it is contained here so that when it does break
 * there is one file to change.
 *
 * The element is created once and reused. Building one per pad hit would allocate on the touch
 * path, which is the one place in PadLab nothing is allowed to allocate.
 */

import { createLogger } from '../core/log';

const log = createLogger('haptics');

let switchElement: HTMLInputElement | null = null;
let switchUnavailable = false;

/** True when `navigator.vibrate` exists — Android and Chromium desktop. */
export function hasVibrationAPI(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}

/**
 * True when Safari's switch control is present, which is what makes the iOS tap possible.
 * Feature-detected off the element itself rather than a version sniff.
 */
export function hasSwitchHaptics(): boolean {
  if (typeof document === 'undefined') return false;
  const probe = document.createElement('input');
  probe.type = 'checkbox';
  // `switch` is a real reflected property only where the control is implemented.
  return 'switch' in probe;
}

/** True when PadLab can produce any physical feedback at all on this device. */
export function hapticsAvailable(): boolean {
  return hasVibrationAPI() || hasSwitchHaptics();
}

function ensureSwitch(): HTMLInputElement | null {
  if (switchElement !== null) return switchElement;
  if (switchUnavailable || typeof document === 'undefined') return null;
  if (!hasSwitchHaptics()) {
    switchUnavailable = true;
    return null;
  }

  const input = document.createElement('input');
  input.type = 'checkbox';
  input.setAttribute('switch', '');
  // Hidden from sight and from assistive technology: it is a haptic actuator, not a control,
  // and a VoiceOver user finding an unlabelled switch in the page would be a real defect.
  input.setAttribute('aria-hidden', 'true');
  input.tabIndex = -1;
  input.style.cssText =
    'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:0';
  document.body.appendChild(input);
  switchElement = input;
  log.debug('Switch haptics armed');
  return input;
}

/**
 * Fires one tap. Call from inside the gesture that caused it — Safari only produces the tick
 * for a toggle it can attribute to a user action.
 *
 * `pattern` is honoured where the Vibration API exists and collapses to a single tap on iOS,
 * which is all the platform offers.
 */
export function playHaptic(pattern: number | number[]): void {
  if (hasVibrationAPI()) {
    navigator.vibrate(pattern);
    return;
  }
  const input = ensureSwitch();
  if (input === null) return;
  // Toggling is what emits the tick; the value itself is meaningless and never read.
  input.checked = !input.checked;
}
